import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Box, ClickAwayListener, InputLabel } from '@mui/material';
import { FormikProps } from 'formik';
import { SxProps, SystemStyleObject } from '@mui/system';
import { TimeValidationError } from '@mui/x-date-pickers/internals/hooks/validation/useTimeValidation';
import { TimeFormat, userPreferredTimeFormat } from '@pharmaplan/common';
import { useAppSelector } from '../../../hooks/useAppSelector';
import genericClasses from '../../../theme/GenericClasses';
import { TextFieldVariants } from '../../../helpers/Constants';
import useTextField from '../../../hooks/useTextField';

import classes from './style';

const { filled, outlined } = TextFieldVariants;
interface ICustomTimePicker {
  label: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>;
  name: string;
  horizontal?: boolean;
  disabled?: boolean;
  variant?: TextFieldVariants;
  onError: (reason: TimeValidationError, value: any) => void;
  error: string;
  minTime?: Date | null;
  maxTime?: Date | null;
  customHandler?: (value?: Date | null) => void;
  placeholder?: string;
  customClass?: SxProps;
  syncHandler?: boolean;
  usePreFilledColors?: boolean;
  textColor?: string | null;
  customTime?: Date | null;
}

const defaultProps = {
  horizontal: false,
  disabled: false,
  minTime: null,
  maxTime: null,
  customClass: {},
};

const CustomTimePicker = ({
  label,
  horizontal,
  minTime,
  maxTime,
  formik,
  variant,
  name,
  placeholder,
  onError,
  error,
  disabled,
  customClass,
  syncHandler,
  customHandler,
  usePreFilledColors,
  customTime = null,
  textColor,
}: ICustomTimePicker) => {
  const [open, setOpen] = useState(false);
  const handleClick = () => {
    setOpen(false);
  };

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const isTwelveHours = timeFormat === TimeFormat.twelveHours;
  const isFilled = filled === variant;
  const isOutlined = outlined === variant;

  const { style } = useTextField({
    value: formik.values[name],
    usePreFilledColors,
  });

  useEffect(() => {
    if (!open) {
      syncHandler && customHandler?.(formik.values[name] || customTime);
      formik.setFieldValue(name, formik.values[name] || customTime);
    }
  }, [open]);

  return (
    <ClickAwayListener onClickAway={handleClick}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box
          component="div"
          sx={[
            horizontal ? classes.horizontalTimePicker : {},
            customClass as SystemStyleObject,
          ]}
        >
          <InputLabel>{label}</InputLabel>
          <TimePicker
            minutesStep={15}
            open={open}
            ampm={isTwelveHours}
            minTime={minTime}
            maxTime={maxTime}
            onOpen={() =>
              setOpen(true)}
            onClose={handleClick}
            ampmInClock
            value={formik.values[name] || customTime}
            disabled={disabled}
            onError={onError}
            onChange={(newValue) => {
              customHandler?.(newValue);
              formik.setFieldValue(name, newValue);
            }}
            renderInput={(params) =>
              (
                <TextField
                  sx={[
                    textColor
                      ? {
                        backgroundColor: 'common.lightBackgroundColor',
                        '& input.Mui-disabled': {
                          '-webkit-text-fill-color': textColor as string,
                        },
                      }
                      : {},
                    isOutlined ? {} : genericClasses.borderNoneTextField,
                    isFilled ? genericClasses.customFilledInput : {},
                    {
                      '& .MuiInputBase-input': style,
                    },
                  ]}
                  variant={variant}
                  helperText={error}
                // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                    placeholder,
                    value: formik.values[name] ? params.inputProps?.value : '',
                  }}
                />
              )}
          />
        </Box>
      </LocalizationProvider>
    </ClickAwayListener>
  );
};

CustomTimePicker.defaultProps = defaultProps;

export default CustomTimePicker;
